<template>
    <nav class="KbBreadcrumbs" :class="{ 'KbBreadcrumbs--compact': compact }" aria-label="Breadcrumb">
        <ol class="KbBreadcrumbs__list">
            <li class="KbBreadcrumbs__item">
                <KbLink class="KbBreadcrumbs__link" :target="{ kind: 'home' }">
                    Help
                </KbLink>
            </li>
            <li v-for="section in sections" :key="section.id" class="KbBreadcrumbs__item">
                <KbLink
                    class="KbBreadcrumbs__link"
                    :target="{ kind: 'section', id: section.id }"
                >
                    {{ section.name }}
                </KbLink>
            </li>
            <li v-if="current" class="KbBreadcrumbs__item">
                <span class="KbBreadcrumbs__current" aria-current="page">{{ current }}</span>
            </li>
        </ol>
    </nav>
</template>

<style lang="scss">
.KbBreadcrumbs {
    font-size: 13px;
    color: var(--eidSecureBlue-75);

    &__list {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px;
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__item {
        display: flex;
        align-items: center;
        min-width: 0;
    }

    &__link {
        color: var(--eidSecureBlue-75);
        text-decoration: none;

        &:hover,
        &:focus-visible {
            color: var(--eidSecureBlue);
            text-decoration: underline;
        }
    }

    &__current {
        color: var(--eidSecureBlue);
    }

    /* The panel is narrow: one line, with each crumb truncated rather than wrapping the
       header onto several rows. */

    &--compact &__list {
        flex-wrap: nowrap;
        overflow: hidden;
    }

    &--compact &__link,
    &--compact &__current {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

/* Stylelint rejects nested sibling selector `& + &::before` with "Unexpected '/'"
   (postcss-selector-parser combinator parsing error on Vue-processed SCSS).
   Use flat selector instead. */

.KbBreadcrumbs__item + .KbBreadcrumbs__item::before {
    content: "›";
    margin-right: 4px;
    color: var(--eidTechGray-75);
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { KbSection } from '@evidentid/tprm-portal-lib/models/kb';

    // Every section crumb is a link; only `current` -- the page the reader is on -- is not.
    // The section page passes its parents as `sections` and its own name as `current`; the
    // help panel passes the whole path and no `current`, since the article title already
    // sits right under the trail there.
    import KbLink from '@/modules/kb/components/KbLink/KbLink.vue';

    export default defineComponent({
        name: 'KbBreadcrumbs',
        components: { KbLink },
        props: {
            sections: {
                type: Array as PropType<KbSection[]>,
                required: true,
            },
            current: {
                type: String,
                default: '',
            },
            compact: {
                type: Boolean,
                default: false,
            },
        },
    });
</script>
